import {
  useActivity,
  useActivityOccurrence,
  type IActivityScopeTab,
} from "@/api/Activities";
import { Trans, useLingui } from "@lingui/react/macro";
import styles from "./ActivityOccurrenceDetails.module.scss";
import { Heading } from "@medoma/components/Heading/Heading";
import { Text, TextWithLineBreaks } from "@medoma/components/Text/Text";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import {
  categorySchema,
  getActivityCategoryConfig,
  measurementsDictionary,
  timeOfDayDictionary,
  timeOfDaySchema,
  type IActivityOccurrence,
} from "@medoma/models/activities";
import { formattedTimeSpan } from "@/components/Time/timeUtils";
import { dateName } from "@/Utils/DateUtils";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { ActionCardLink } from "@/components/ActionCard/ActionCard";
import { Tabs, TabList, TabPanel, Tab } from "@medoma/components/Tabs";
import { humanFormat } from "@medoma/models/date-and-time";
import { DurationText } from "./DurationText";
import { ScheduleSummary } from "./ScheduleSummary";
import { CloseActivitySeriesButton } from "./CloseActivitySeriesButton";
import { deducedError } from "@/Utils/ErrorUtils";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import { useState } from "react";
import z from "zod";

const EmptyValue = () => (
  <Text element="p" color="faded">
    -
  </Text>
);

export const ActivityOccurrenceEdit = () => {
  const { t } = useLingui();
  const { patientId, activityId, occurrenceId } = z
    .object({
      patientId: z.string(),
      activityId: z.string(),
      occurrenceId: z.string(),
    })
    .parse(useParams());
  const {
    data: activityOccurrence,
    isPending,
    isError,
    error,
  } = useActivityOccurrence(activityId, occurrenceId);
  const [activeTab, setActiveTab] = useState<IActivityScopeTab>("occurrence");
  const navigate = useNavigate();

  const navigateToActivitiesList = () =>
    navigate(`/patients/${patientId}/activities`);

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} />;
  }

  if (isError) {
    return <ErrorMessage message={deducedError(error)} />;
  }

  return (
    <div className={styles.activityOccurrenceDetails}>
      <Heading level="h2">{activityOccurrence.title}</Heading>

      {activityOccurrence.recurring ? (
        <Tabs>
          <TabList stateOwner="react">
            <Tab
              isSelected={activeTab === "occurrence"}
              onClick={() => setActiveTab("occurrence")}
            >
              <Trans>Tillfälle</Trans>
            </Tab>
            <Tab
              isSelected={activeTab === "series"}
              onClick={() => setActiveTab("series")}
            >
              <Trans>Serie</Trans>
            </Tab>
          </TabList>
          <TabPanel>
            <div className={styles.tabPanelContent}>
              {activeTab === "series" ? (
                <SeriesTabContent
                  activityOccurrence={activityOccurrence}
                  onCloseActivitySeriesSuccess={navigateToActivitiesList}
                />
              ) : (
                <ActivityOccurrenceInformation
                  activityOccurrence={activityOccurrence}
                  isRecurringTab={false}
                />
              )}
            </div>
          </TabPanel>
        </Tabs>
      ) : (
        <ActivityOccurrenceInformation
          activityOccurrence={activityOccurrence}
          isRecurringTab={false}
        />
      )}
    </div>
  );
};

const SeriesTabContent = ({
  activityOccurrence,
  onCloseActivitySeriesSuccess,
}: {
  activityOccurrence: IActivityOccurrence;
  onCloseActivitySeriesSuccess: () => void;
}) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const {
    data: activity,
    isPending,
    isError,
    error,
  } = useActivity(activityOccurrence.activityId);

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} />;
  }

  if (isError) {
    return <ErrorMessage message={deducedError(error)} />;
  }

  return (
    <div className={styles.seriesTabContent}>
      <CloseActivitySeriesButton
        id={activity.id}
        status={activity.status}
        onSuccess={async () => {
          await queryClient.invalidateQueries();
          onCloseActivitySeriesSuccess();
        }}
      />
      <ActivityOccurrenceInformation
        activityOccurrence={activityOccurrence}
        isRecurringTab={true}
      />
    </div>
  );
};

const ActivityOccurrenceInformation = ({
  activityOccurrence,
  isRecurringTab,
}: {
  activityOccurrence: IActivityOccurrence;
  isRecurringTab: boolean;
}) => {
  const { t } = useLingui();
  const {
    data: activity,
    isPending,
    isError,
    error,
  } = useActivity(activityOccurrence.activityId);

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} />;
  }

  if (isError) {
    return <ErrorMessage message={deducedError(error)} />;
  }

  const activityHasPatient =
    "patient" in activityOccurrence && Boolean(activityOccurrence.patient);
  const categoryConfig = getActivityCategoryConfig({
    category: activityOccurrence.category,
    activityHasPatient,
  });
  const isPatientMeasurementTask =
    activityOccurrence.category === categorySchema.enum.PatientMeasurementTask;
  const isHomeVisit =
    activityOccurrence.category === categorySchema.enum.HomeVisit;

  // A non-recurring activity has no tab split, so both scopes render together.
  // For a recurring activity the selected tab decides which scope is visible.
  const viewingOccurrenceScope = !isRecurringTab;
  const viewingSeriesScope = isRecurringTab || !activityOccurrence.recurring;
  const showRecurringSchedule = isRecurringTab && activityOccurrence.recurring;

  return (
    <section className={styles.editableFields}>
      {viewingOccurrenceScope ? (
        <>
          <ActionCardLink title={t`Kommentar`} to="edit-note">
            {activityOccurrence.note ? (
              <TextWithLineBreaks color="faded">
                {activityOccurrence.note}
              </TextWithLineBreaks>
            ) : (
              <EmptyValue />
            )}
          </ActionCardLink>

          <ActionCardLink title={t`Starttid`} to="edit-start-time">
            <Text element="p" color="faded">
              {dateName(activityOccurrence.start)}
            </Text>
            <Text element="p" color="faded">
              {activityOccurrence.timeOfDay === timeOfDaySchema.enum.Any
                ? t(timeOfDayDictionary.Any.long)
                : formattedTimeSpan(
                    activityOccurrence.start,
                    activityOccurrence.end,
                  )}
            </Text>
          </ActionCardLink>
        </>
      ) : null}

      {viewingSeriesScope ? (
        <>
          <ActionCardLink title={t`Titel`} to="edit-title">
            <Text element="p" color="faded">
              {activityOccurrence.title}
            </Text>
          </ActionCardLink>

          <ActionCardLink title={t`Beskrivning`} to="edit-description">
            {activityOccurrence.description ? (
              <TextWithLineBreaks color="faded">
                {activityOccurrence.description}
              </TextWithLineBreaks>
            ) : (
              <EmptyValue />
            )}
          </ActionCardLink>

          {categoryConfig.allowsDuration ? (
            <ActionCardLink title={t`Tidsåtgång`} to="edit-duration">
              <Text element="p" color="faded">
                <DurationText duration={activityOccurrence.duration} />
              </Text>
            </ActionCardLink>
          ) : null}

          {categoryConfig.allowsHidden ? (
            <ActionCardLink title={t`Dold för patienten`} to="edit-hidden">
              <Text element="p" color="faded">
                {activityOccurrence.hidden ? (
                  <Trans>Ja</Trans>
                ) : (
                  <Trans>Nej</Trans>
                )}
              </Text>
            </ActionCardLink>
          ) : null}

          {isHomeVisit ? (
            <ActionCardLink
              title={t`Dubbelbemanning`}
              to="edit-double-staffing"
            >
              <Text element="p" color="faded">
                {activityOccurrence.doubleStaffing ? (
                  <Trans>Ja</Trans>
                ) : (
                  <Trans>Nej</Trans>
                )}
              </Text>
            </ActionCardLink>
          ) : null}

          {categoryConfig.allowsCompetence &&
          "requiredCompetences" in activityOccurrence ? (
            <ActionCardLink
              title={t`Kompetensbehov`}
              to="edit-required-competences"
            >
              {activityOccurrence.requiredCompetences.length > 0 ? (
                <ul className={styles.competenceChips}>
                  {activityOccurrence.requiredCompetences.map((competence) => (
                    <li key={competence}>
                      <CompetenceChip competence={competence} state="neutral" />
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyValue />
              )}
            </ActionCardLink>
          ) : null}

          {isPatientMeasurementTask ? (
            <ActionCardLink title={t`Mätvärden`} to="edit-measurements">
              <Text element="p" color="faded">
                {activityOccurrence.measurements.length > 0
                  ? activityOccurrence.measurements
                      .map((measurement) =>
                        t(measurementsDictionary[measurement]),
                      )
                      .join(", ")
                  : "-"}
              </Text>
            </ActionCardLink>
          ) : null}
        </>
      ) : null}

      {showRecurringSchedule ? (
        <>
          <ActionCardLink title={t`Datum`} to="edit-end-date">
            <Text element="p" color="faded">
              {"start" in activity.schedule
                ? humanFormat(activity.schedule.start, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "-"}
            </Text>
          </ActionCardLink>
          <ActionCardLink title={t`Schemaläggning`} to="edit-schedule">
            <ScheduleSummary schedule={activity.schedule} />
          </ActionCardLink>
        </>
      ) : null}
    </section>
  );
};
